import {
  ArrowRight,
  Minus,
  Plus,
  Undo2,
  Bookmark,
  Check,
  Clock3,
  ShieldCheck,
  RotateCcw,
} from "lucide-react";
import { SYSTEM_INFO, ZONE_BY_ID, ZONES } from "../data/city";
import { allocatedTotal, canDecide } from "../simulation/engine";
import { RESOURCE_LIMIT, SYSTEMS } from "../simulation/types";
import { useCity } from "../state/CityContext";
import { clock, StatusPill } from "./Primitives";
export function RespondPanel({ onOutcome }: { onOutcome: () => void }) {
  const { state, dispatch, current, decide } = useCity();
  const selected = state.selected ? ZONE_BY_ID[state.selected] : null;
  const zoneState = selected
    ? current.zones.find((z) => z.id === selected.id)!
    : null;
  const used = allocatedTotal(current.allocations);
  return (
    <section
      className="mode-panel respond-panel"
      aria-label="Operational response"
    >
      <span className="eyebrow panel-kicker">
        <span className="section-number">03</span> RESPOND / CITY OPERATIONS
      </span>
      <h1>
        Every decision
        <br />
        <em>makes a difference.</em>
      </h1>
      <p className="panel-intro">
        Eight shared crews. Six areas. One connected city.
      </p>
      <div className="resource-pool">
        <div>
          <ShieldCheck size={19} />
          <span>
            <strong>{RESOURCE_LIMIT - used}</strong> crews available
          </span>
          <small>{used} deployed</small>
        </div>
        <div className="crew-slots" aria-label={`${used} of 8 crews deployed`}>
          {Array.from({ length: RESOURCE_LIMIT }, (_, i) => (
            <i key={i} className={i < used ? "used" : ""} />
          ))}
        </div>
      </div>
      {selected && zoneState ? (
        <div className="allocation-detail">
          <div className="allocation-head">
            <span className="eyebrow">AREA RESPONSE</span>
            <select
              aria-label="Response area"
              value={selected.id}
              onChange={(e) =>
                dispatch({
                  type: "select",
                  zone: e.target.value as typeof selected.id,
                })
              }
            >
              {ZONES.map((z) => (
                <option value={z.id} key={z.id}>
                  {z.short}
                </option>
              ))}
            </select>
          </div>
          <h2>{selected.name}</h2>
          {SYSTEMS.map((s) => {
            const n = current.allocations[selected.id][s];
            return (
              <div className="allocation-row" key={s}>
                <span className={`system-symbol ${s}`} />
                <div>
                  <b>{SYSTEM_INFO[s].crew}</b>
                  <span>{Math.round(zoneState[s])}% stress</span>
                </div>
                <div className="stepper">
                  <button
                    aria-label={`Withdraw ${SYSTEM_INFO[s].crew} from ${selected.name}`}
                    disabled={
                      state.run.minute === 120 ||
                      !canDecide(
                        state.run.decisions,
                        state.run.minute,
                        selected.id,
                        s,
                        -1,
                      )
                    }
                    onClick={() => decide(selected.id, s, -1)}
                  >
                    <Minus size={13} />
                  </button>
                  <output aria-label={`${SYSTEM_INFO[s].label} crews`}>
                    {n}
                  </output>
                  <button
                    aria-label={`Deploy ${SYSTEM_INFO[s].crew} to ${selected.name}`}
                    disabled={
                      state.run.minute === 120 ||
                      !canDecide(
                        state.run.decisions,
                        state.run.minute,
                        selected.id,
                        s,
                        1,
                      )
                    }
                    onClick={() => decide(selected.id, s, 1)}
                  >
                    <Plus size={13} />
                  </button>
                </div>
              </div>
            );
          })}
          <div className="response-time">
            <Clock3 size={14} />
            <span>
              5 min to arrive. Full effect after 25 min.
              <br />
              Maximum three crews per system and area.
            </span>
          </div>
          {state.run.minute === 120 && (
            <p className="small-note">
              The experiment has ended. Rewind the timeline before allocating
              new crews.
            </p>
          )}
          <button
            className="text-button"
            onClick={() => dispatch({ type: "select", zone: null })}
          >
            Back to incident queue <ArrowRight size={14} />
          </button>
        </div>
      ) : (
        <div className="incident-section">
          <div className="section-heading">
            <span className="eyebrow">PRIORITY QUEUE</span>
            <span className="mini-label">
              {current.incidents.length} active
            </span>
          </div>
          {current.incidents.length ? (
            <div className="incident-list">
              {current.incidents.map((incident, i) => (
                <button
                  className="incident-item"
                  key={incident.id}
                  onClick={() => {
                    dispatch({ type: "select", zone: incident.zone });
                    dispatch({ type: "layer", layer: incident.system });
                  }}
                >
                  <span className="incident-number">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <strong>{ZONE_BY_ID[incident.zone].short}</strong>
                    <span>{incident.title}</span>
                    <StatusPill severity={incident.severity} />
                  </div>
                  <span className="incident-score">
                    {Math.round(incident.severity)}
                    <small>%</small>
                    {incident.crews > 0 && (
                      <b>
                        {incident.crews} crew{incident.crews > 1 ? "s" : ""}
                      </b>
                    )}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="calm-queue">
              <Check size={25} />
              <h2>No active incidents.</h2>
              <p>
                Introduce a scenario or advance time. You can also position
                crews proactively.
              </p>
              <button
                className="text-button"
                onClick={() => dispatch({ type: "mode", mode: "simulate" })}
              >
                Open scenario lab <ArrowRight size={14} />
              </button>
            </div>
          )}
          <label className="preposition">
            Position crews in an area
            <select
              aria-label="Position crews proactively"
              value=""
              onChange={(e) => {
                if (e.target.value)
                  dispatch({
                    type: "select",
                    zone: e.target.value as (typeof ZONES)[number]["id"],
                  });
              }}
            >
              <option value="">Choose an area…</option>
              {ZONES.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      <div className="response-actions">
        <button
          className="secondary-button"
          onClick={() => dispatch({ type: "compare" })}
        >
          <Bookmark size={15} />
          {state.comparison ? "Update pinned plan" : "Pin plan for comparison"}
        </button>
        <button className="text-button" onClick={onOutcome}>
          Compare outcomes <ArrowRight size={14} />
        </button>
      </div>
      {state.run.decisions.length > 0 && (
        <div className="decision-journal">
          <div className="section-heading">
            <span className="eyebrow">DECISION JOURNAL</span>
            <button
              className="text-button"
              onClick={() => dispatch({ type: "undo" })}
            >
              <Undo2 size={12} /> Undo latest
            </button>
          </div>
          {state.run.decisions
            .slice(-4)
            .reverse()
            .map((d) => (
              <div className="journal-item" key={d.id}>
                <span>{clock(d.minute)}</span>
                <p>
                  {d.delta === 1 ? "Deploy" : "Withdraw"}{" "}
                  {SYSTEM_INFO[d.system].label.toLowerCase()} crew{" "}
                  <b>{ZONE_BY_ID[d.zone].short}</b>
                </p>
              </div>
            ))}
          <button
            className="text-button"
            onClick={() => dispatch({ type: "reset" })}
          >
            <RotateCcw size={12} /> Clear response and rewind
          </button>
        </div>
      )}
    </section>
  );
}
